/**
 * =============================================================================
 * Hook: useSelectedAffiliateCampaign
 * Versão: 1.0.0
 *
 * Descrição:
 *   Gerencia qual campanha do afiliado está "ativa" para as ações rápidas
 *   da home (Copiar / WhatsApp / QR / FAB de compartilhar).
 *
 *   - Persiste a seleção em localStorage por afiliado
 *     (`affiliate:home:selectedCampaignId:<affiliateId>`).
 *   - Auto-corrige quando a campanha persistida não existe mais na lista
 *     (foi desativada, removida, etc.) — cai para a primeira disponível.
 *   - Expõe `hasMultiple` para a UI decidir se renderiza o seletor.
 *
 *   Tudo client-side; nenhuma chamada à RPC ou ao DB.
 * =============================================================================
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { MyCampaignLink } from '@/hooks/useAffiliateMyCampaigns';

/** Monta a chave de storage isolada por afiliado. */
function storageKey(affiliateId: string | null | undefined): string | null {
  if (!affiliateId) return null;
  return `affiliate:home:selectedCampaignId:${affiliateId}`;
}

/** Lê com segurança do localStorage (SSR-safe / try-catch contra browsers privados). */
function readPersisted(key: string | null): string | null {
  if (!key || typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

/** Escreve com segurança no localStorage. */
function writePersisted(key: string | null, value: string | null): void {
  if (!key || typeof window === 'undefined') return;
  try {
    if (value === null) {
      window.localStorage.removeItem(key);
    } else {
      window.localStorage.setItem(key, value);
    }
  } catch {
    /* ignora erros de storage (modo privado, quota, etc.) */
  }
}

export interface UseSelectedAffiliateCampaignReturn {
  /** Campanha atualmente selecionada (já validada contra a lista). */
  selectedCampaign: MyCampaignLink | undefined;
  /** Define manualmente a campanha ativa. */
  setSelectedCampaignId: (id: string) => void;
  /** True quando o afiliado tem 2+ campanhas (vale a pena mostrar o seletor). */
  hasMultiple: boolean;
}

/**
 * Recebe a lista atual de campanhas + id do afiliado e devolve a campanha
 * "selecionada" coerente. Mantém estado local + persistência idempotente.
 */
export function useSelectedAffiliateCampaign(
  campaigns: MyCampaignLink[] | undefined,
  affiliateId: string | null | undefined,
): UseSelectedAffiliateCampaignReturn {
  const key = storageKey(affiliateId);

  // Estado local começa lendo do storage; se não houver, fica null e o
  // efeito abaixo escolhe a 1ª disponível.
  const [selectedId, setSelectedId] = useState<string | null>(() =>
    readPersisted(key),
  );

  // Sincroniza o estado quando o afiliado muda (troca de conta).
  useEffect(() => {
    setSelectedId(readPersisted(key));
  }, [key]);

  // Auto-corrige: se a campanha persistida não existe mais ou ainda não
  // foi escolhida nada, cai para a 1ª da lista.
  useEffect(() => {
    if (!campaigns || campaigns.length === 0) return;
    const stillValid = selectedId
      ? campaigns.some((c) => c.campaign_id === selectedId)
      : false;
    if (!stillValid) {
      const fallback = campaigns[0].campaign_id;
      setSelectedId(fallback);
      writePersisted(key, fallback);
    }
  }, [campaigns, selectedId, key]);

  const selectedCampaign = useMemo<MyCampaignLink | undefined>(() => {
    if (!campaigns || campaigns.length === 0) return undefined;
    return (
      campaigns.find((c) => c.campaign_id === selectedId) ?? campaigns[0]
    );
  }, [campaigns, selectedId]);

  const setSelectedCampaignId = useCallback(
    (id: string) => {
      setSelectedId(id);
      writePersisted(key, id);
    },
    [key],
  );

  return {
    selectedCampaign,
    setSelectedCampaignId,
    hasMultiple: (campaigns?.length ?? 0) > 1,
  };
}
